import { NOT_ACCESSIBLE_TO_CLIENT } from "components/common/noData/constants"
import { navBarSections, navBarViews } from "extensions/utils/Classes"
import { useMemo } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import useAllowedPaths from "./useAllowedPaths"
import useExtension from "./useExtension"

const navBarViewsMapping = Object.freeze({
  [navBarSections.adminSection]: navBarViews.internalView,
  [navBarSections.cmsSection]: navBarViews.internalView,
  [navBarSections.clientSection]: navBarViews.clientView
})

const checkIfUrlIsAllowed = (
  navItem,
  allowedPaths,
  allowedPortalComponents,
  sectionName
) =>
  Boolean(
    allowedPaths.includes(navItem.url) &&
      (sectionName !== navBarSections.clientSection ||
        Boolean(navItem.enableForAllProjects) ||
        Boolean(allowedPortalComponents[navItem.portalComponentKey || ""]))
  )

export default function () {
  const location = useLocation()
  const extension = useExtension()
  const allowedPaths = useAllowedPaths()
  const allowedPortalComponents = useSelector(
    (state) => state.user.allowedPortalComponents
  )
  const navSections = useMemo(
    () => extension.modules.nav || [],
    [extension.modules.nav]
  )

  const allowedNavSections = useMemo(
    () =>
      Object.entries(navSections)
        .map(([sectionName, navItems]) => ({
          sectionName,
          sectionItems: navItems
            .map((navItem) => ({
              ...navItem,
              links: navItem.links
                .filter((link) =>
                  checkIfUrlIsAllowed(
                    link,
                    allowedPaths,
                    allowedPortalComponents,
                    sectionName
                  )
                )
                .map((link) => {
                  return {
                    ...link,
                    hiddenForClient:
                      allowedPortalComponents[link.portalComponentKey || ""] ===
                      NOT_ACCESSIBLE_TO_CLIENT
                  }
                })
            }))
            .map((navItem) => ({
              ...navItem,
              hiddenForClient: navItem.links.every(
                (link) => link.hiddenForClient
              )
            }))
            .filter((navItem) => navItem.links.length > 0)
        }))
        .filter(({ sectionItems }) => sectionItems.length > 0),
    [allowedPaths, allowedPortalComponents, navSections]
  )

  const navViews = useMemo(
    () =>
      Object.fromEntries(
        Object.values(navBarViews).map((view) => [
          view,
          allowedNavSections.filter(
            ({ sectionName }) => navBarViewsMapping[sectionName] === view
          )
        ])
      ),
    [allowedNavSections]
  )

  const selectedNavBarView = useMemo(
    () =>
      navBarViewsMapping[
        Object.entries(navSections).find(([_, navItems]) =>
          Boolean(
            navItems.find(
              (navItem) =>
                navItem.url === location.pathname ||
                navItem.links?.find(
                  (subItem) => subItem.url === location.pathname
                )
            )
          )
        )?.[0] || navBarSections.clientSection
      ],
    [location.pathname, navSections]
  )

  return { navViews, selectedNavBarView }
}
